/**
 * The wordmark: `PIXEL//TD`, drawn one pixel at a time.
 *
 * Iconography in this build comes from the vendored HackerNoon pixel-icon
 * library and nowhere else — no hand-authored SVG, no icon fonts, no emoji, and
 * that rule has held every audit. A logo is not iconography, so this does not
 * reach into that set for a mark that is not in it. It is drawn the way
 * `glyphs.ts` draws the creatures: as characters in a grid, in the same
 * encoding, so anyone who can read one file can edit the other.
 *
 *   `.`  empty
 *   `#`  body        → the bright channel
 *   `+`  accent      → the accent channel
 *
 * Nor is it typed in Departure Mono and screenshotted. The letterforms are 7px
 * tall on a 5px body, which is the same budget the creature glyphs work in, and
 * a 5×7 letter drawn by hand sits at exactly the resolution the rest of the
 * screen is drawn at. A font scaled down to 7px is a font that has been
 * hinted at 7px by somebody else.
 *
 * The two slashes carry `+` and everything else carries `#`, which is the only
 * decision in the mark: `PIXEL` and `TD` are the name and `//` is the seam
 * between them.
 *
 * Plain data. Nothing here imports Three.js or touches the DOM — `ui/Logo.ts`
 * paints it, and `tests/logo.test.ts` reads it without a browser.
 */

/** One letter, as tall as the mark and as wide as it needs to be. */
interface Cell {
  readonly w: number;
  readonly rows: readonly string[];
}

/** Every letterform is seven rows. The mark is seven pixels tall, full stop. */
export const LOGO_HEIGHT = 7;

const P: Cell = { w: 5, rows: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'] };
const I: Cell = { w: 3, rows: ['###', '.#.', '.#.', '.#.', '.#.', '.#.', '###'] };
const X: Cell = { w: 5, rows: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'] };
const E: Cell = { w: 5, rows: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'] };
const L: Cell = { w: 5, rows: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'] };
const T: Cell = { w: 5, rows: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'] };
const D: Cell = { w: 5, rows: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'] };
/* The seam. Accent channel, and one column narrower than a letter so the pair
   reads as a single mark rather than as two more characters in the word. */
const SLASH: Cell = { w: 3, rows: ['..+', '..+', '.+.', '.+.', '.+.', '+..', '+..'] };

/** A one-pixel gutter, which is all a 5px letter needs to stand apart. */
const GAP: Cell = { w: 1, rows: ['.', '.', '.', '.', '.', '.', '.'] };

const SEQUENCE: readonly Cell[] = [
  P, GAP, I, GAP, X, GAP, E, GAP, L, GAP,
  SLASH, GAP, SLASH, GAP,
  T, GAP, D,
];

/** Composed once. `47 × 7`, top row first, in the encoding above. */
export const LOGO_ROWS: readonly string[] = Array.from(
  { length: LOGO_HEIGHT },
  (_, y) => SEQUENCE.map((c) => c.rows[y]).join(''),
);

/** Width in pixels, derived rather than counted by hand. */
export const LOGO_WIDTH = SEQUENCE.reduce((n, c) => n + c.w, 0);
